import { isRouteErrorResponse } from "@remix-run/react";
import { ErrorInfo, ProductionMessage } from "../types";

const ERROR_MESSAGES = {
  404: {
    message: "404",
    details: "The requested page could not be found.",
  },
  403: {
    message: "403",
    details: "You don't have permission to access this resource.",
  },
  500: {
    message: "500",
    details: "Something went wrong on our end. Please try again later.",
  },
  401: {
    message: "401",
    details: "You don't have permission to access this resource.",
  },
  connection: {
    message: "Connection Error",
    details:
      "Unable to connect to the server. Please check your internet connection and try again.",
  },
  validation: {
    message: "Validation Error",
    details:
      "The information provided is invalid. Please check your input and try again.",
  },
  authentication: {
    message: "Authentication Required",
    details: "Please log in to access this page.",
  },
  generic: {
    message: "Something Went Wrong",
    details:
      "We encountered an unexpected error. Our team has been notified and is working on a fix.",
  },
  unknown: {
    message: "Oops!",
    details: "An unexpected error occurred. Please try again later.",
  },
} as const;

export function getProductionMessage(
  error: unknown,
  status?: number,
): ProductionMessage {
  // Handle RouteErrorResponse
  if (isRouteErrorResponse(error)) {
    const statusCode = error.status;
    if (statusCode === 404) return ERROR_MESSAGES[404];
    if (statusCode === 403) return ERROR_MESSAGES[403];
    if (statusCode === 500) return ERROR_MESSAGES[500];
    return {
      message: String(statusCode),
      details:
        error.statusText || "An error occurred while processing your request.",
    };
  }

  // Handle Error instances
  if (error instanceof Error) {
    const errorName = error.name;
    const errorMessage = error.message.toLowerCase();

    // Network errors
    if (
      errorName === "TypeError" &&
      (errorMessage.includes("fetch") ||
        errorMessage.includes("network") ||
        errorMessage.includes("connection"))
    ) {
      return ERROR_MESSAGES.connection;
    }

    // Validation errors
    if (
      errorName === "ValidationError" ||
      errorMessage.includes("validation")
    ) {
      return ERROR_MESSAGES.validation;
    }

    // Authentication errors
    if (
      errorName === "UnauthorizedError" ||
      errorMessage.includes("unauthorized")
    ) {
      return ERROR_MESSAGES.authentication;
    }

    // Generic errors
    return ERROR_MESSAGES.generic;
  }

  // Handle status code if provided
  if (status) {
    if (status === 404) return ERROR_MESSAGES[404];
    if (status === 500) return ERROR_MESSAGES[500];
  }

  // Unknown errors
  return ERROR_MESSAGES.unknown;
}

function stringifyError(error: unknown): string {
  try {
    if (error === null) return "null";
    if (error === undefined) return "undefined (no error object provided)";
    return String(error);
  } catch {
    return "Unable to stringify error";
  }
}

function getRawErrorString(error: unknown): string | undefined {
  try {
    if (error === null) return "null";
    if (error === undefined) return undefined;
    try {
      return JSON.stringify(error, null, 2);
    } catch {
      return String(error);
    }
  } catch {
    return undefined;
  }
}

export function getDevelopmentErrorInfo(
  error: unknown,
  status?: number,
): ErrorInfo {
  if (isRouteErrorResponse(error)) {
    return {
      userMessage: String(error.status),
      userDetails: error.statusText || "Route error occurred",
      devTitle: `RouteErrorResponse: ${error.status}`,
      devDetails: {
        errorType: "RouteErrorResponse",
        errorName: "RouteErrorResponse",
        errorMessage: error.statusText || String(error.status),
        statusCode: error.status,
        rawError: error.data ? JSON.stringify(error.data, null, 2) : undefined,
      },
    };
  }

  if (error instanceof Error) {
    const causeString =
      error.cause instanceof Error
        ? error.cause.message
        : error.cause
          ? String(error.cause)
          : undefined;

    return {
      userMessage: error.name || "Error",
      userDetails: error.message || "An error occurred",
      devTitle: `${error.name}: ${error.message}`,
      devDetails: {
        errorType: error.constructor.name,
        errorName: error.name,
        errorMessage: error.message,
        statusCode: status,
        cause: causeString,
        stack: error.stack,
        rawError:
          error.cause && typeof error.cause === "object"
            ? JSON.stringify(error.cause, null, 2)
            : undefined,
      },
    };
  }

  // Handle string errors
  if (typeof error === "string") {
    return {
      userMessage: "Error",
      userDetails: error,
      devTitle: `Error: ${error}`,
      devDetails: {
        errorType: "string",
        errorName: "String Error",
        errorMessage: error,
        statusCode: status,
      },
    };
  }

  // Unknown error type
  const errorString = stringifyError(error);
  const rawErrorString = getRawErrorString(error);

  return {
    userMessage: "Unknown Error",
    userDetails: "An unknown error occurred",
    devTitle: `Unknown Error: ${errorString}`,
    devDetails: {
      errorType:
        typeof error === "object" && error !== null
          ? error.constructor?.name || typeof error
          : typeof error,
      errorName: "Unknown",
      errorMessage: errorString,
      statusCode: status,
      rawError: rawErrorString,
    },
  };
}

export function getErrorStatus(
  error: unknown,
  status?: number,
): number | undefined {
  if (isRouteErrorResponse(error)) {
    return error.status;
  }
  if (error instanceof Error && "status" in error) {
    const errorWithStatus = error as Error & { status: number };
    return errorWithStatus.status;
  }
  return status;
}
